import { useState } from 'react';
import { format, subMonths, addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { calendarActionsService } from '@/services/calendar-actions.service';
import { toast } from '@/utils/toast';

interface DownloadCalendarDialogProps {
  isOpen: boolean;
  onClose: () => void;
  calendarId: number;
  calendarName: string;
}

type ExportFormat = 'csv' | 'json' | 'ics';

export const DownloadCalendarDialog = ({
  isOpen,
  onClose,
  calendarId,
  calendarName,
}: DownloadCalendarDialogProps) => {
  const { t } = useTranslation(['calendar', 'common']);
  const [startDate, setStartDate] = useState(format(subMonths(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(addMonths(new Date(), 2), 'yyyy-MM-dd'));
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [isLoading, setIsLoading] = useState(false);

  const FORMAT_OPTIONS: Array<{ value: ExportFormat; label: string; description: string }> = [
    { value: 'csv', label: t('format_csv'), description: t('format_csv_desc') },
    { value: 'json', label: t('format_json'), description: t('format_json_desc') },
    { value: 'ics', label: t('format_ics'), description: t('format_ics_desc') },
  ];

  if (!isOpen) return null;

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      // Build boundaries from LOCAL date components so the exported range covers
      // the full local start and end days (avoids UTC-midnight parsing of
      // "yyyy-MM-dd" strings clipping local-midnight entries in positive-offset
      // timezones like Europe/Berlin).
      let startIso: string;
      let endIso: string;
      if (startDate && endDate) {
        const [sy, sm, sd] = startDate.split('-').map(Number);
        const [ey, em, ed] = endDate.split('-').map(Number);
        startIso = new Date(sy, sm - 1, sd, 0, 0, 0, 0).toISOString();
        endIso = new Date(ey, em - 1, ed, 23, 59, 59, 999).toISOString();
      } else {
        startIso = new Date(startDate).toISOString();
        endIso = new Date(endDate).toISOString();
      }

      const blob = await calendarActionsService.exportCalendar(
        calendarId,
        startIso,
        endIso,
        exportFormat
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${calendarName}-${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('calendar_exported_success'));
      onClose();
    } catch (error) {
      toast.error(t('calendar_export_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => !isLoading && onClose()}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-full">
            <Icon name={ICONS.DOWNLOAD} size="md" color="text-primary-600" ariaHidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('calendar_download_title')}</h2>
            <p className="text-sm text-gray-500">{calendarName}</p>
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('start_date')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('end_date')}</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Format Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('export_format')}</label>
          <div className="space-y-2">
            {FORMAT_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  'flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors',
                  exportFormat === option.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="radio"
                  name="exportFormat"
                  value={option.value}
                  checked={exportFormat === option.value}
                  onChange={() => setExportFormat(option.value)}
                  className="mt-0.5"
                  disabled={isLoading}
                />
                <div>
                  <span className="font-medium text-gray-900">{option.label}</span>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-1.5 sm:gap-2">
          <CancelButton onClick={onClose} disabled={isLoading} />
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={isLoading}
          >
            {isLoading ? t('downloading') : t('download')}
          </Button>
        </div>
      </div>
    </div>
  );
};
